import { useCallback, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { CustomToast } from "../ui/CustomToast";
import { SSEManagerClass } from "../../utils/useSSEEvent";
import { accountsApi, campaignsApi, BASE_API_URL } from "../../api";
import { useAppDispatch, useAppSelector } from "../../hooks";
import {
  AccountPlanStatusData,
  AnalysisData,
  AnalysisStatus,
  GenerationData,
  ImportDataStatus,
  MismatchData,
  RecalculationData,
  SSEDataTypes,
  SSEEvents,
  ToastMessage,
} from "../../types/companyTypes";
import {
  setImportDataStatus,
  setMismatchData,
  setIsFetchTableMembers,
  setRecalculatingStatus,
  setStatus,
  setUpdatedCompanies,
  setUpdatedStatus,
} from "../../features/companySlice";
import { useParams } from "react-router-dom";
import { CampaignId, UpdatedAccountListItem } from "../../types";

const SSE_DEBUG = true;
const log = (...args: unknown[]) => {
  if (SSE_DEBUG) {
    // eslint-disable-next-line no-console
    console.log(`%c[SSE ${new Date().toISOString()}]`, "color:#0aa", ...args);
  }
};
const warn = (...args: unknown[]) => {
  if (SSE_DEBUG) {
    // eslint-disable-next-line no-console
    console.warn(`%c[SSE ${new Date().toISOString()}]`, "color:#e90", ...args);
  }
};

const sseManager = SSEManagerClass.getInstance();

export const SSEManager = () => {
  const { campaign_id } = useParams();

  const campaignIdRef = useRef(campaign_id);

  log("SSEManager mounted", {
    initialCampaignId: campaign_id,
    refInitializedWith: campaignIdRef.current,
  });

  const dispatch = useAppDispatch();
  const token = useAppSelector((state) => state.auth.accessToken) as string;
  const refreshToken = useAppSelector(
    (state) => state.auth.refreshToken,
  ) as string;

  useEffect(() => {
    log("campaign_id changed", {
      prev: campaignIdRef.current,
      next: campaign_id,
      prevType: typeof campaignIdRef.current,
      nextType: typeof campaign_id,
    });

    if (campaign_id !== undefined) {
      campaignIdRef.current = campaign_id;
    } else {
      warn("campaign_id is undefined from useParams, keeping ref", {
        keptRef: campaignIdRef.current,
      });
    }
  }, [campaign_id]);

  const isMismatch = (dataCampaignId: string): boolean => {
    if (!campaignIdRef.current) {
      warn("isMismatch: ref is undefined, allowing event through", {
        dataCampaignId,
      });
      return false;
    }
    const mismatch = dataCampaignId !== campaignIdRef.current;
    if (mismatch) {
      warn("isMismatch: DROPPED", {
        dataCampaignId,
        ref: campaignIdRef.current,
      });
    }
    return mismatch;
  };

  const generateHandler = (event: SSEEvents, data: GenerationData) => {
    log("generateHandler", { event, data });
    dispatch(setStatus({ [event]: data }));

    const { status, message } = data;

    if (status === "failed") {
      toast.error(
        <CustomToast
          type={"error"}
          text={
            message ||
            "Something went wrong during the analysis of your data. Please try again"
          }
          title={"Analysis failed"}
        />,
      );
    }
  };

  const analysisHandler = (event: SSEEvents, data: AnalysisData) => {
    log("analysisHandler received", {
      event,
      data,
      dataCampaignId: data.campaign_id,
      currentCampaignId: campaignIdRef.current,
      match: data.campaign_id === campaignIdRef.current,
    });

    if (isMismatch(data.campaign_id)) return;

    const { status, message, progress, account_id, item, name } = data;

    dispatch(setStatus({ [event]: data }));
    let updatedData: UpdatedAccountListItem = {
      status,
      progress,
      id: account_id,
      company: name,
      message,
    };

    if (item) {
      updatedData = { ...updatedData, ...item };
    }

    log("analysisHandler dispatching setUpdatedCompanies", updatedData);
    dispatch(setUpdatedCompanies(updatedData));

    if ([AnalysisStatus.failed, AnalysisStatus.completed].includes(status)) {
      log("analysisHandler terminal status, invalidating tags", status);
      dispatch(
        campaignsApi.util.invalidateTags([
          "Campaign",
          "summary",
          "company",
          "accounts-for-scraping",
          "activities",
          "role-members",
        ]),
      );
      dispatch(setIsFetchTableMembers(true));
      dispatch(accountsApi.util.invalidateTags(["analytics"]));
    }
  };

  const scrapingHandler = (event: SSEEvents, data: AnalysisData) => {
    log("scrapingHandler received", {
      event,
      data,
      dataCampaignId: data.campaign_id,
      currentCampaignId: campaignIdRef.current,
      match: data.campaign_id === campaignIdRef.current,
    });

    if (isMismatch(data.campaign_id)) return;

    const { status, message, progress, account_id, item, name } = data;

    dispatch(setStatus({ [event]: data }));
    let updatedData: UpdatedAccountListItem = {
      [event]: status,
      progress,
      id: account_id,
      company: name,
      message,
    };

    if (item) {
      updatedData = { ...updatedData, ...item };
    }

    log("scrapingHandler dispatching setUpdatedCompanies", updatedData);
    dispatch(setUpdatedCompanies(updatedData));
    dispatch(setStatus({ [event]: data }));

    if (
      [
        AnalysisStatus.failed,
        AnalysisStatus.completed,
        AnalysisStatus.completed_no_data,
      ].includes(status)
    ) {
      log("scrapingHandler terminal status, invalidating tags", status);
      dispatch(
        campaignsApi.util.invalidateTags([
          "summary",
          "role-members",
          "activities",
          "company",
        ]),
      );
      dispatch(setIsFetchTableMembers(true));
    }
  };

  const recalculationStatusHandler = (
    event: SSEEvents,
    data: RecalculationData,
  ) => {
    log("recalculationStatusHandler received", { event, data });
    if (isMismatch(data.campaign_id)) return;

    const { status, message } = data;

    if (status === AnalysisStatus.failed) {
      toast.error(
        <CustomToast
          type={"error"}
          text={message || "Something went wrong. Please try again"}
          title={"Recalculating scores failed"}
        />,
      );
      dispatch(setRecalculatingStatus("stop"));
    } else if (
      [AnalysisStatus.completed, AnalysisStatus.completed_no_data].includes(
        status,
      )
    ) {
      dispatch(setRecalculatingStatus("stop"));
    }
  };

  const mismatchHandler = (event: SSEEvents, data: MismatchData) => {
    log("mismatchHandler received", { event, data });
    if (isMismatch(data.campaign_id)) return;

    dispatch(setMismatchData(data));

    if (!data.mismatches.is_syncing) {
      dispatch(accountsApi.util.invalidateTags(["list-view", "analytics"]));
      dispatch(setIsFetchTableMembers(true));
      dispatch(
        campaignsApi.util.invalidateTags([
          "role-members",
          "company",
          "summary",
          "Campaign",
        ]),
      );
    }
  };

  const importDataHandler = (event: SSEEvents, data: ImportDataStatus) => {
    log("importDataHandler received", { event, data });
    dispatch(setImportDataStatus(data));
  };

  const accountPlanHandler = (
    event: SSEEvents,
    data: AccountPlanStatusData,
  ) => {
    log("accountPlanHandler received", {
      event,
      data,
      match: data.campaign_id === campaignIdRef.current,
    });
    if (isMismatch(data.campaign_id ?? "")) return;

    dispatch(setStatus({ [event]: data }));
    dispatch(setUpdatedStatus({ [data.account_id]: data.status }));

    dispatch(accountsApi.util.invalidateTags(["list-view"]));
    dispatch(setIsFetchTableMembers(true));
    dispatch(
      campaignsApi.util.invalidateTags([
        "company",
        "summary",
        "Campaign",
        "activities",
      ]),
    );
  };

  const toastMessageHandler = (event: SSEEvents, data: ToastMessage) => {
    log("toastMessageHandler received", { event, data });
    if (isMismatch(data.campaign_id)) return;

    try {
      const { title, description, type } = data;
      toast[type](<CustomToast type={type} title={title} text={description} />);
    } catch (e) {
      warn("toastMessageHandler failed to render toast", e);
    }
  };

  const usersActivitiesHandler = (event: SSEEvents, data: CampaignId) => {
    log("usersActivitiesHandler received", { event, data });
    if (isMismatch(data.campaign_id)) return;

    dispatch(campaignsApi.util.invalidateTags(["activities"]));
  };

  const playbookHandler = (event: SSEEvents, data: AccountPlanStatusData) => {
    log("playbookHandler received", { event, data });
    if (isMismatch(data.campaign_id ?? "")) return;

    dispatch(setUpdatedStatus({ [data.member_id]: data.status }));

    dispatch(setIsFetchTableMembers(true));
    dispatch(
      campaignsApi.util.invalidateTags([
        "activities",
        "role-members",
        "company",
      ]),
    );
  };

  const manager = {
    [SSEEvents.analysis_status]: analysisHandler,
    [SSEEvents.scraping_status]: scrapingHandler,
    [SSEEvents.scraping_update_status]: scrapingHandler,
    [SSEEvents.generation_status]: generateHandler,
    [SSEEvents.recalculation_status]: recalculationStatusHandler,
    [SSEEvents.mismatch_update]: mismatchHandler,
    [SSEEvents.import_data_status]: importDataHandler,
    [SSEEvents.account_plan_status]: accountPlanHandler,
    [SSEEvents.campaign_user_stats]: usersActivitiesHandler,
    [SSEEvents.sdr_playbook_status]: playbookHandler,
    [SSEEvents.toast_message]: toastMessageHandler,
  };

  const handleMessage = (sseData: SSEDataTypes) => {
    const { event, data } = sseData;

    log("RAW SSE message received", {
      event,
      data,
      rawPayload: JSON.stringify(sseData),
    });

    const eventHandler = manager[event] as (
      event: SSEEvents,
      data: unknown,
    ) => void;

    if (!eventHandler) {
      warn("No handler registered for event type", event, sseData);
      return;
    }

    try {
      eventHandler(event, data);
    } catch (e) {
      warn("Handler threw an exception", event, e, data);
    }
  };

  const connect = useCallback(
    (reason: string) => {
      const last_notification_id = localStorage.getItem("last_notification_id");
      const query = last_notification_id
        ? `?last_notification_id=${last_notification_id}`
        : "";
      const url = `${BASE_API_URL}/me/notifications${query}`;

      log("connect() called", {
        reason,
        url,
        last_notification_id,
        hasToken: !!token,
        tokenPrefix: token ? token.slice(0, 12) : null,
        hasRefreshToken: !!refreshToken,
        alreadyConnected: sseManager.isConnected,
        campaignId: campaignIdRef.current,
        userAgent: navigator.userAgent,
        visibilityState: document.visibilityState,
        online: navigator.onLine,
      });

      sseManager.connect({
        url,
        token,
        refreshToken,
        onMessage: handleMessage,
        onError: (err?: unknown) => {
          warn("sseManager onError fired", err, {
            reason,
            url,
            last_notification_id,
            visibilityState: document.visibilityState,
          });
        },
        dispatch: dispatch,
      });

      log("connect() finished issuing connect call", {
        reason,
        isConnectedAfterCall: sseManager.isConnected,
      });
    },
    [token, refreshToken],
  );

  const reconnectOnVisible = useCallback(() => {
    log("visibilitychange fired", {
      visibilityState: document.visibilityState,
      isConnected: sseManager.isConnected,
    });
    if (document.visibilityState === "visible") {
      connect("visibilitychange->visible");
    }
  }, [connect]);

  useEffect(() => {
    document.addEventListener("visibilitychange", reconnectOnVisible);
    log("mounted, visibilitychange listener attached", {
      last_notification_id: localStorage.getItem("last_notification_id"),
    });

    return () => {
      document.removeEventListener("visibilitychange", reconnectOnVisible);
      log("unmounting, clearing last_notification_id from localStorage", {
        prevValue: localStorage.getItem("last_notification_id"),
      });
      localStorage.removeItem("last_notification_id");
    };
  }, [reconnectOnVisible]);

  useEffect(() => {
    log("mount effect - checking connection state", {
      isConnected: sseManager.isConnected,
    });
    if (!sseManager.isConnected) {
      connect("mount");
    } else {
      warn(
        "mount effect - sseManager already reports isConnected=true, skipping connect. " +
          "If notifications are stuck, this stale isConnected flag could be the bug.",
      );
    }
  }, []);

  return null;
};
